import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Loading.css';

interface LoadingProps {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

const Loading: React.FC<LoadingProps> = ({ isLoading, setIsLoading }) => {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("Initializing core system...");

  useEffect(() => {
    let startTimestamp: number | null = null;
    const duration = 1200; // 1.2 seconds smooth loading time
    let animationFrameId: number;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const elapsed = timestamp - startTimestamp;
      const progressVal = Math.min((elapsed / duration) * 100, 100);
      
      setProgress(Math.floor(progressVal));

      if (progressVal < 20) {
        setStatusText("Bootstrapping core protocols...");
      } else if (progressVal < 45) {
        setStatusText("Loading Framer Motion & design tokens...");
      } else if (progressVal < 70) {
        setStatusText("Calibrating ML algorithms & neural nodes...");
      } else if (progressVal < 90) {
        setStatusText("Injecting UX sugar & layout grids...");
      } else {
        setStatusText("System ready. Launching...");
      }

      if (elapsed < duration) {
        animationFrameId = requestAnimationFrame(step);
      } else {
        setProgress(100);
        setTimeout(() => {
          setIsLoading(false);
        }, 200);
      }
    };

    animationFrameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrameId);
  }, [setIsLoading]);

  const containerVariants = {
    initial: { 
      y: "0%" 
    },
    exit: {
      y: "-100%",
      transition: { 
        duration: 0.75, 
        ease: [0.76, 0, 0.24, 1] 
      }
    }
  };

  const centerCoreVariants = {
    initial: { scale: 0.6, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 120, damping: 14 }
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isLoading && (
        <motion.div 
          className="loading-screen"
          variants={containerVariants}
          initial="initial"
          exit="exit"
          key="loading"
        >
          <div className="loading-content">
            <motion.div 
              className="loading-gyro-container"
              variants={centerCoreVariants}
              initial="initial"
              animate="animate"
            >
              {/* Central Glowing Energy Core */}
              <motion.div 
                className="gyro-core"
                animate={{
                  scale: [1, 1.15, 0.95, 1],
                  boxShadow: [
                    "0 0 20px rgba(99, 102, 241, 0.4)",
                    "0 0 40px rgba(139, 92, 246, 0.6)",
                    "0 0 20px rgba(6, 182, 212, 0.4)",
                    "0 0 20px rgba(99, 102, 241, 0.4)"
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
              
              {/* Nested SVG Gyro Spinners */}
              <svg className="gyro-svg" viewBox="0 0 100 100">
                {/* Outer Ring */}
                <motion.circle
                  cx="50"
                  cy="50"
                  r="44"
                  stroke="var(--accent-color)"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="180, 80"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                />
                {/* Middle Ring (Reverse Direction) */}
                <motion.circle
                  cx="50"
                  cy="50"
                  r="36"
                  stroke="#06b6d4"
                  strokeWidth="1.5"
                  fill="none"
                  strokeDasharray="100, 100"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
                {/* Inner dashed ring */}
                <motion.circle
                  cx="50"
                  cy="50"
                  r="28"
                  stroke="rgba(255, 255, 255, 0.15)"
                  strokeWidth="1"
                  fill="none"
                  strokeDasharray="6, 8"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                />
              </svg>
            </motion.div>

            {/* Futuristic HUD Display */}
            <div className="loader-hud">
              <div className="loader-percentage">
                <span className="number-glow">{progress.toString().padStart(3, '0')}</span>
                <span className="percent-sign">%</span>
              </div>
              <div className="loader-status">
                <span className="status-cursor">&gt;&nbsp;</span>
                <span className="status-text">{statusText}</span>
              </div>
              
              {/* Progress track line */}
              <div className="hud-progress-track">
                <motion.div 
                  className="hud-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Loading;
